import { mainSliceActions } from "@common-reducers/MainReducer";
import { clearAuthError, setAuthError } from "@common-reducers/UserReducer";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import React, { useCallback, useEffect, useState } from "react";
import { authService, AuthCredentials } from "@/common/bt-auth/auth-service";
import { useRouter } from "@common-reducers/hooks/use-router";
import { namespace } from "@common-config/constants";
import { useTranslation } from "react-i18next";
import GoogleLoginWrapper from "../google-auth-button/google-login-wrapper";
import { PasskeySignInButton } from "./passkey-signin-button";
import { Mail, Lock } from 'lucide-react';
import { cn } from "@/lib/utils";
import { isExtensionContext } from "@common-services/detect-context";
import { getRuntimeUrl } from '@common-services/storage';
import { flowPath, isClearFlow } from '@common/utils/clear-flow';
import { CLEAR_BRAND_LOGO, brandName } from '@common/config/clear-brand';

export const LoginForm = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const analyticsService = useAnalyticsService();

  const validateLoginData = authService.validateLoginData.bind(authService);
  const [authData, setAuthData] = useState<AuthCredentials>({ email: '', password: '' });
  const [showValidation, setShowValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const authError = useAppSelector(state => state.UserReducer.authError);
  const errorFormColor = '#f72371';
  const router = useRouter();

  const handleInputChange = ({ target }: { target: { name: string; value: string } }) => {
    setAuthData(prev => ({ ...prev, [target.name]: target.value }));
    dispatch(clearAuthError());
  };

  const switchFormState = (target: 'RESET_PASSWORD' | 'SIGNUP') => {
    const eventMap: Record<'RESET_PASSWORD' | 'SIGNUP', string> = {
      RESET_PASSWORD: "switch_to_forgot_password",
      SIGNUP: "switch_to_signup"
    };

    if (eventMap[target]) {
      analyticsService.event(eventMap[target], {
        category: "access",
        action: `switch to ${target.toLowerCase().replace('_', ' ')}`
      });
      dispatch(mainSliceActions.setFormState(target));

      if (!isExtensionContext()) {
        const currentUrlParams = new URLSearchParams(window.location.search);
        const paramsString = currentUrlParams.toString();
        const newPath = flowPath(`/signup${paramsString ? `?${paramsString}` : ''}`);
        router.navigate(newPath);
      }
    }
  };

  const handleLogin = useCallback(async () => {
    // Validate form data
    const validation = validateLoginData(authData);
    if (!validation.isValid) {
      // The inline per-field validation below already renders the localized
      // "required" messages for empty fields, so don't also push them into
      // authError (that produced a duplicate message). Only surface via
      // authError the one validation error inline can't show: a filled-in
      // but malformed email.
      setShowValidation(true);
      const emailFilledButInvalid =
        !!authData.email && !authService.validateEmail(authData.email);
      dispatch(
        emailFilledButInvalid
          ? setAuthError('Please enter a valid email address')
          : clearAuthError()
      );
      return;
    }

    setShowValidation(false);
    setIsLoading(true);

    try {
      const result = await authService.login(authData, dispatch);
      
      if (!result.success) {
        dispatch(setAuthError(result.error ?? 'An unexpected error occurred'));
      }
    } catch (error) {
      dispatch(setAuthError('An unexpected error occurred'));
    } finally {
      setIsLoading(false);
    }
  }, [authData, validateLoginData, dispatch, router]);

  useEffect(() => {
    document.title = t('loginForm.pageTitle');
    const userExists = new URLSearchParams(window.location.search).get('hasUser');
    let userEmail = new URLSearchParams(window.location.search).get('email');

    if (userExists === '2') {
      setTimeout(() => {
        switchFormState("SIGNUP");
      }, 0);
    } else if (userExists === '1' && userEmail) {
      const safeEmail = userEmail.replace(/ /g, '+');
      setAuthData(prev => ({ ...prev, email: safeEmail }));
    }
  }, [t]);

  const inputStyle = (key: keyof AuthCredentials) => ({
    borderColor: showValidation && !authData[key] ? errorFormColor : '',
    marginBottom: key === "email" ? 5 : undefined,
    fontSize: '16px',
    padding: '0 10px',
  });

  return (
    <div className="p-6 space-y-6 max-w-lg mx-auto w-full">
      {/* Logo and Title */}
      <div className="flex flex-col items-center space-y-4">
        <img
          src={isClearFlow() ? CLEAR_BRAND_LOGO : getRuntimeUrl("/img/blueticks_logo_icon.png")}
          alt={`${brandName()} Logo`}
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
          }}
        />
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--color-text, #111827)' }}>{brandName()}</h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted, #6b7280)' }}>{t('loginForm.header')}</p>
        </div>
      </div>

      {/* Form */}
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
        }}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleLogin();
          }
        }}
      >
        <div className="space-y-2">
          <label htmlFor="login-email" className="text-sm font-medium leading-none block mb-2" style={{ color: 'var(--color-text, #111827)' }}>
            {t('loginForm.emailLabel')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--color-text-muted, #6b7280)' }} />
            <input
              id="login-email"
              data-testid="login-email-input"
              name="email"
              type="email"
              placeholder={t('loginForm.emailPlaceholder')}
              value={authData.email}
              onChange={handleInputChange}
              className={cn(
                "bt-access-input flex h-10 w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
                showValidation && !authData.email ? 'border-red-500' : ''
              )}
              style={{
                borderColor: showValidation && !authData.email ? errorFormColor : 'var(--color-border, #e5e7eb)',
                backgroundColor: 'var(--color-surface, #ffffff)',
                color: 'var(--color-text, #111827)',
              } as React.CSSProperties}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 1px rgba(59, 130, 246, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = '';
              }}
            />
          </div>
          {showValidation && !authData.email && (
            <p className="text-sm text-red-500">{t('loginForm.emailRequired')}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="login-password" className="text-sm font-medium leading-none block mb-2" style={{ color: 'var(--color-text, #111827)' }}>
            {t('loginForm.passwordLabel')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--color-text-muted, #6b7280)' }} />
            <input
              id="login-password"
              data-testid="login-password-input"
              name="password"
              type="password"
              placeholder={t('loginForm.passwordPlaceholder')}
              value={authData.password}
              onChange={handleInputChange}
              className={cn(
                "bt-access-input flex h-10 w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
                showValidation && !authData.password ? 'border-red-500' : ''
              )}
              style={{
                borderColor: showValidation && !authData.password ? errorFormColor : 'var(--color-border, #e5e7eb)',
                backgroundColor: 'var(--color-surface, #ffffff)',
                color: 'var(--color-text, #111827)',
              } as React.CSSProperties}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 1px rgba(59, 130, 246, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = '';
              }}
            />
          </div>
          {showValidation && !authData.password && (
            <p className="text-sm text-red-500">{t('loginForm.passwordRequired')}</p>
          )}
        </div>

        {authError && (
          <p className="text-sm text-red-500" style={{ marginTop: '-8px', marginBottom: '8px' }}>{authError}</p>
        )}

        <button
          type="button"
          onClick={() => switchFormState("RESET_PASSWORD")}
          className="text-sm font-medium hover:underline"
          style={{ color: 'var(--color-primary, #3b82f6)' }}
        >
          {t('loginForm.forgotPassword')}
        </button>

        <button
          type="button"
          data-testid="login-sign-in-button"
          onClick={handleLogin}
          disabled={isLoading || !authData.email || !authData.password}
          className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--color-primary, #3b82f6)',
            color: 'var(--color-primary-contrast, #ffffff)',
          }}
        >
          {isLoading ? t('loginForm.loggingIn') : t('loginForm.loginButton')}
        </button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" style={{ borderColor: 'var(--color-border, #e5e7eb)' }} />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2" style={{ backgroundColor: 'var(--color-surface, #ffffff)', color: 'var(--color-text-muted, #6b7280)' }}>
            {t('loginForm.orContinueWith')}
          </span>
        </div>
      </div>

      {/* Passkey sign-in: hidden unless the flag is on and the browser supports it */}
      <PasskeySignInButton />

      {/* Google Sign In */}
      <div className="flex justify-center">
        <GoogleLoginWrapper />
      </div>

      {/* Footer */}
      <div className="text-center text-sm">
        <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>
          {t('loginForm.dontHaveAccount')} {' '}
        </span>
        <button
          type="button"
          className="font-medium hover:underline"
          onClick={() => switchFormState("SIGNUP")}
          style={{ color: 'var(--color-primary, #3b82f6)' }}
        >
          {t('loginForm.signUp')}
        </button>
      </div>
    </div>
  );
};